import type {
  BotReport,
  BotRun,
  Collaborator,
  ConversationEvent,
  ApiKeySummary,
  WebhookDelivery,
  WebhookSubscription,
  AuditPage,
  PlatformOverview,
  PlatformUser,
  TenantMoneyRow,
  AdminChannel, AdminErrorEvent, AdminJobRun, AdminOverview, AdminTenant,
  Agent, AdminTemplate, AuthUser, CannedReply, Conversation, ForwardResult, Message, Note, Page, PermissionDefinition, Portal,
  Role, RoutingFlow, Tag, Template, TemplateDraft, TenantSettings, WorkingHour,
  AgentReportRow, BillingProfile, CategoryPrice, Invoice, LedgerEntry, ReportOverview,
  SlaPolicy, SlaStatus, Statement, ChannelCommerce, ComplianceInfo, CatalogProduct, CatalogState, ProductInput, TemplateReportRow, TopUpOrder, CheckoutResult, PaymentState, Wallet,
  ShopSettings, ShopSettingsInput, CustomerGroup, CustomerGroupInput, ProductCategory, ProductDetail, ProductListItem, ShopProductInput, PriceRow, PriceRowInput, ShopImportPreview,
  AgentGroup, AssignmentRule, TeamMember, MobileDevice, MetaApp, ChannelCheck, ScheduledMessage,
  Contact, ContactField, ContactSegment, ImportJob, ImportPreview, ImportProblem, Segment, SegmentMember, SegmentRule,
  MemberQuery, MemberAction, RecipientAction,
  Campaign, CampaignClickSummary, CampaignDetail, CampaignEstimate, CampaignPreview,
  CampaignRecipient, VariableBinding,
  BotAction, BotEntry, BotFlow, BotFlowSummary, BotFunnel, BotSessionRow, FlowReport,
  TenantChannel, TemplateSendExtras,
  NotificationPreferences,
  Plan, PlanBody, TenantSubscriptionView, MyPlan,
} from './types';

/**
 * The single HTTP client both portals use.
 *
 * The access token is held in a module variable — never localStorage — so a script
 * injected into the page cannot read it out of storage after the fact. It survives
 * only as long as the tab's JavaScript context; a reload recovers a session from the
 * httpOnly refresh cookie instead, which JavaScript cannot touch at all.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** Where the API lives, for screens that show a person a call to paste. */
export const API_BASE_URL: string = BASE_URL || window.location.origin;

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Called when a 401 could not be recovered by a refresh: the session is over, and
 * the shell must show the login form instead of a page of error boxes. Set by the
 * AuthProvider; a module-level hook because `send` has no React context.
 */
let sessionLost: (() => void) | null = null;
export function onSessionLost(handler: (() => void) | null): void {
  sessionLost = handler;
}

export interface ChannelCreated {
  id: number; verified: boolean; problem: string | null; isActive: boolean;
  verifiedName: string | null; displayPhoneNumber: string | null;
  qualityRating: string | null; nameStatus: string | null; sendRatePerSecond: number;
  subscribed: boolean; subscribeProblem: string | null;
  registered: boolean; registerProblem: string | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
    readonly correlationId: string | null = null,
    /**
     * The parsed problem body, for the few answers that carry more than a code
     * and a title — a 409 on template create names the existing template's id so
     * the editor can link to it. Null when the body was not JSON.
     */
    readonly body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface Options extends Omit<RequestInit, 'body'> {
  /**
   * The request body as an object — send() serialises it. Typed `object` rather
   * than `unknown` on purpose: it makes passing an already-stringified body a
   * compile error instead of a 400 the server reports as a malformed request,
   * which is exactly how the template send shipped broken once.
   *
   * FormData is the exception and passes through untouched: the browser has to
   * set the multipart boundary itself, and a hand-written Content-Type is
   * silently wrong.
   */
  body?: object | FormData;
  /** Internal: prevents a refresh loop when the refresh call itself returns 401. */
  retrying?: boolean;
  /**
   * No bearer token, and no refresh-and-replay on a 401.
   *
   * For the two endpoints only somebody who cannot sign in ever calls. Without it a
   * forgotten-password request from a signed-out browser triggers a refresh attempt
   * that fails, which is a wasted round trip and a confusing 401 in the console.
   */
  anonymous?: boolean;
}

/**
 * One trip, including the refresh-and-replay. Returns the Response so the callers
 * that want a file rather than JSON can have one without duplicating any of this —
 * which is what the media upload used to do, and it quietly lacked the 401 retry
 * every other call has.
 */
async function send(path: string, options: Options = {}): Promise<Response> {
  const { body, headers, retrying, anonymous, ...rest } = options;
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    // Sends the refresh cookie on /api/v1/auth calls. Harmless elsewhere: the
    // cookie is path-scoped, so it is not attached to ordinary API traffic.
    credentials: 'include',
    headers: {
      ...(accessToken && !anonymous ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(body === undefined || isForm ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
  });

  // The access token lasts 15 minutes, so an expiry mid-session is normal rather
  // than exceptional. Refresh once and replay, and collapse concurrent refreshes
  // into one so a page with several queries does not rotate the token N times.
  if (response.status === 401 && !retrying && !path.startsWith('/api/v1/auth/')) {
    const recovered = await refreshOnce();
    if (recovered) return send(path, { ...options, retrying: true });
    sessionLost?.();
  }

  if (!response.ok) {
    const correlationId = response.headers.get('X-Correlation-Id');
    // statusText is empty under HTTP/2, and a screen that shows `error.message`
    // then shows nothing at all.
    let message = response.statusText || `Request failed (${response.status})`;
    let code: string | null = null;
    let body: Record<string, unknown> | null = null;
    try {
      const problem = (await response.json()) as { title?: string; detail?: string; code?: string };
      // A plan refusal carries the sentence that says what to do about it in
      // `detail`; shown with the title, because "all 5 agents are in use" without
      // "add one for ₹500 a month" is half an answer.
      if (problem.title && problem.detail && problem.code === 'plan_limit') message = `${problem.title} ${problem.detail}`;
      else if (problem.title) message = problem.title;
      else if (problem.detail) message = problem.detail;
      if (problem.code) code = problem.code;
      body = problem as Record<string, unknown>;
    } catch { /* not JSON */ }
    throw new ApiError(response.status, message, code, correlationId, body);
  }

  return response;
}

async function request<T>(path: string, options: Options = {}): Promise<T> {
  const response = await send(path, { ...options, headers: { Accept: 'application/json', ...options.headers } });
  // 204 and 202 come back empty, and so can any success behind a proxy; parsing
  // "" as JSON throws, which turned "we sent the reset email" into "try again".
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text === '' ? undefined : JSON.parse(text)) as T;
}

/** For endpoints that answer with a file. The filename comes from the server. */
async function requestBlob(path: string, options: Options = {}): Promise<{ blob: Blob; fileName: string }> {
  const response = await send(path, options);
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
  return {
    blob: await response.blob(),
    fileName: match?.[1] ? decodeURIComponent(match[1]) : 'download.csv',
  };
}

function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      accessToken = (await refreshOrRetry()).accessToken;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Two tabs that wake together both present the same refresh cookie; the server
 * rotates it for the first and answers the second with `refresh_raced`, leaving
 * the cookie jar holding the winner's new token. Trying again a moment later
 * with that cookie succeeds, so the loser is not signed out for having been a
 * few milliseconds behind.
 */
export async function refreshOrRetry(): Promise<LoginResponse> {
  try {
    return await request<LoginResponse>('/api/v1/auth/refresh', { method: 'POST' });
  } catch (error) {
    if (!(error instanceof ApiError && error.code === 'refresh_raced')) throw error;
    await new Promise((resolve) => setTimeout(resolve, 400 + Math.random() * 400));
    return request<LoginResponse>('/api/v1/auth/refresh', { method: 'POST' });
  }
}

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: AuthUser;
}

export const authApi = {
  login: (email: string, password: string, portal: Portal) =>
    request<LoginResponse>('/api/v1/auth/login', { method: 'POST', body: { email, password, portal } }),
  refresh: () => refreshOrRetry(),
  logout: () => request<void>('/api/v1/auth/logout', { method: 'POST' }),
};

export interface ConversationCounts {
  /** M10, for the filter panel. `open` is status 0 and `closed` is status 2 —
      the panel's own words, and deliberately separate keys from `resolved` so
      selecting one does not highlight the other. */
  open: number;
  closed: number;
  /** Computed, never stored: targets are policy and elapsed times are facts.
      The same rule as the list's `slaBreached` filter, and a check asserts the
      two agree — two copies of a rule disagree eventually, and when these do the
      badge says nine and the screen shows six. */
  slaBreached: number;
  all: number; unread: number; awaiting: number;
  resolved: number; expired: number; unassigned: number; mine: number;
  mentions: number;

  /** The filter panel's four groups, each with its own statuses. One grouped
      query on the server rather than twenty calls from the panel. */
  scopes?: Record<'all' | 'mine' | 'sla' | 'unassigned', ScopeCounts>;
}

/**
 * The statuses inside one group.
 *
 * `closed` is optional and absent on purpose for two of the four: on the API
 * `assigned=unassigned` already means "and not resolved", and `slaBreached`
 * means "not resolved yet", so a Closed row under either would be a permanent
 * zero. Optional here rather than always-zero, so the panel omits the row
 * instead of drawing one that can never do anything.
 */
export interface ScopeCounts {
  open: number;
  closed?: number;
  awaiting: number;
  unread: number;
  all: number;
}

/** Both ends optional; the server defaults to the last thirty days. */
function range(from?: string, to?: string): string {
  const parts = [from && `from=${from}`, to && `to=${to}`].filter(Boolean);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const inboxApi = {
  /** `query` comes from the saved view; `cursor` continues the previous page. */
  conversations: (query?: URLSearchParams, cursor?: string | null) => {
    const params = new URLSearchParams(query);
    if (cursor) params.set('cursor', cursor);
    const suffix = params.toString();
    return request<Page<Conversation>>(`/api/v1/conversations${suffix ? `?${suffix}` : ''}`);
  },
  counts: () => request<ConversationCounts>('/api/v1/conversations/counts'),
  /** One conversation in the list's shape, or null when it does not exist or
      this agent may not see it. For `?open=<id>` when the row is not on the
      page the inbox happens to be showing. */
  conversation: async (id: number) => {
    const page = await request<Page<Conversation>>(`/api/v1/conversations?id=${id}&take=1`);
    return page.items.find((c) => c.id === id) ?? null;
  },

  /** Opens a conversation with a number, whether or not it is a contact yet.
      Creates nothing on Meta's side and sends nothing — it makes the row an
      agent needs somewhere to type into. */
  startChat: (waId: string, channelId: number) =>
    request<{ conversationId: number; contactId: number; waId: string }>(
      '/api/v1/conversations/start', { method: 'POST', body: { waId, channelId } }),
  /* ------------------------------------------------- the list's selection --
     One call over a set, not a call per row. Forty conversations assigned one at
     a time is forty round trips and forty chances to half-finish.

     There is no bulk delete and its absence is deliberate — see
     BulkInboxEndpoints. `close` is what stands in its place. */

  bulkRead: (ids: number[]) =>
    request<{ changed: number }>('/api/v1/conversations/bulk/read', { method: 'POST', body: { ids } }),

  bulkAssign: (ids: number[], agentId: number | null) =>
    request<{ changed: number }>('/api/v1/conversations/bulk/assign', { method: 'POST', body: { ids, agentId } }),

  bulkStatus: (ids: number[], status: number) =>
    request<{ changed: number }>('/api/v1/conversations/bulk/status', { method: 'POST', body: { ids, status } }),

  bulkTag: (ids: number[], tagId: number) =>
    request<{ changed: number }>('/api/v1/conversations/bulk/tags', { method: 'POST', body: { ids, tagId } }),
  /** `q` searches what was said inside this thread — distinct from the list's
      search, which matches contacts by name and number. */
  messages: (id: number, q?: string) =>
    request<{ items: Message[]; notes: Note[]; hasMore: boolean }>(
      `/api/v1/conversations/${id}/messages${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  /** scheduledFor is a UTC ISO string; omit it to send now. replyToMessageId quotes one of this thread's messages. */
  send: (conversationId: number, body: string, idempotencyKey: string, scheduledFor?: string, replyToMessageId?: number | null) =>
    request<{ messageId: number; status: string; scheduledFor: string | null }>('/api/v1/messages', {
      method: 'POST',
      body: { conversationId, body, idempotencyKey, scheduledFor, replyToMessageId: replyToMessageId ?? null },
    }),

  /**
   * Forwards one message into other chats. Each target is its own send with its
   * own answer — a closed window or a blocked contact refuses one chat, not the
   * batch — so the result is a list, not a status.
   */
  forward: (messageId: number, conversationIds: number[]) =>
    request<{ results: ForwardResult[] }>(`/api/v1/messages/${messageId}/forward`, {
      method: 'POST',
      body: { conversationIds },
    }),

  scheduled: (conversationId?: number) =>
    request<ScheduledMessage[]>(
      `/api/v1/scheduled${conversationId ? `?conversationId=${conversationId}` : ''}`,
    ),

  cancelScheduled: (id: number) =>
    request<void>(`/api/v1/scheduled/${id}`, { method: 'DELETE' }),

  /** Uploads to Meta and stores a copy; returns the id to send it by. */
  upload: (conversationId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ mediaAssetId: number; kind: string; fileName: string }>(
      `/api/v1/media?conversationId=${conversationId}`,
      { method: 'POST', body: form },
    );
  },
  sendMedia: (conversationId: number, mediaAssetId: number, caption: string | null, idempotencyKey: string, replyToMessageId?: number | null) =>
    request<{ messageId: number; status: string }>('/api/v1/messages/media', {
      method: 'POST',
      body: { conversationId, mediaAssetId, caption, idempotencyKey, replyToMessageId: replyToMessageId ?? null },
    }),
  update: (id: number, patch: { status?: number; assignedAgentId?: number | null; unassign?: boolean }) =>
    request<void>(`/api/v1/conversations/${id}`, { method: 'PATCH', body: patch }),
  markRead: (id: number) => request<void>(`/api/v1/conversations/${id}/read`, { method: 'POST' }),

  tags: () => request<Tag[]>('/api/v1/tags'),

  cannedReplies: () => request<CannedReply[]>('/api/v1/canned-replies'),

  /** `mediaAssetId`: an upload to attach (null = none on create; on update null = unchanged, 0 = remove). */
  createCannedReply: (shortcut: string, body: string, mediaAssetId: number | null = null) =>
    request<CannedReply>('/api/v1/canned-replies', { method: 'POST', body: { shortcut, body, mediaAssetId } }),

  updateCannedReply: (id: number, shortcut: string, body: string, mediaAssetId: number | null = null) =>
    request<CannedReply>(`/api/v1/canned-replies/${id}`, { method: 'PATCH', body: { shortcut, body, mediaAssetId } }),

  /**
   * An asset id whose WhatsApp media id works on this number. A file uploaded on
   * one number cannot be sent from another; the server clones it on first use.
   */
  mediaForChannel: (mediaAssetId: number, channelId: number) =>
    request<{ mediaAssetId: number }>(`/api/v1/media/${mediaAssetId}/for-channel/${channelId}`, { method: 'POST' }),

  // ------------------------------------------------------------ catalogue --

  /** The mirrored catalogue, refreshed first when older than half an hour. */
  catalogProducts: (q?: string) =>
    request<{ items: CatalogProduct[]; state: CatalogState }>(`/api/v1/catalogue/products${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  syncCatalogue: () =>
    request<{ items: CatalogProduct[]; state: CatalogState }>('/api/v1/catalogue/sync', { method: 'POST' }),
  createProduct: (body: ProductInput) =>
    request<CatalogProduct>('/api/v1/catalogue/products', { method: 'POST', body }),
  updateProduct: (retailerId: string, body: ProductInput) =>
    request<CatalogProduct>(`/api/v1/catalogue/products/${encodeURIComponent(retailerId)}`, { method: 'PATCH', body }),
  deleteProduct: (retailerId: string) =>
    request<void>(`/api/v1/catalogue/products/${encodeURIComponent(retailerId)}`, { method: 'DELETE' }),
  /** A product picture, stored where Meta can fetch it; returns the URL to put on the product. */
  uploadProductImage: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ imageUrl: string }>('/api/v1/catalogue/images', { method: 'POST', body: form });
  },

  /**
   * The catalogue button (no ids, wholeCatalogue), one product card, or a list
   * of up to thirty. Free-form: needs the 24-hour window like text.
   */
  sendProducts: (body: {
    conversationId: number; body: string; header?: string | null; footer?: string | null;
    retailerIds: string[]; wholeCatalogue: boolean; idempotencyKey: string;
  }) => request<{ messageId: number; status: string }>('/api/v1/messages/products', { method: 'POST', body }),

  deleteCannedReply: (id: number) =>
    request<void>(`/api/v1/canned-replies/${id}`, { method: 'DELETE' }),

  addNote: (conversationId: number, body: string, mentionedAgentIds: number[]) =>
    request<Note>(`/api/v1/conversations/${conversationId}/notes`, {
      method: 'POST',
      body: { body, mentionedAgentIds },
    }),

  editNote: (id: number, body: string) =>
    request<{ id: number; body: string; editedAt: string } | void>(`/api/v1/notes/${id}`, {
      method: 'PATCH',
      body: { body },
    }),

  deleteNote: (id: number) => request<void>(`/api/v1/notes/${id}`, { method: 'DELETE' }),

  availability: () => request<{ availability: number; changedAt: string | null }>('/api/v1/me/availability'),

  setAvailability: (available: boolean) =>
    request<{ available: boolean }>('/api/v1/me/availability', { method: 'PUT', body: { available } }),

  notificationPreferences: () =>
    request<NotificationPreferences>('/api/v1/me/notification-preferences'),

  /**
   * The whole set, every time. A PATCH that merged field by field would let two
   * tabs each save half of what their user saw, leaving a state neither of them
   * chose — eleven switches and a Save button is one intention.
   */
  setNotificationPreferences: (preferences: NotificationPreferences) =>
    request<NotificationPreferences>('/api/v1/me/notification-preferences', {
      method: 'PUT',
      body: preferences,
    }),

  /** Pushes one notification at this agent's own connections and nobody else's. */
  testNotification: () =>
    request<void>('/api/v1/me/notification-preferences/test', { method: 'POST' }),

  wallet: () => request<{ balance: number; isActive: boolean; lowBalanceAlert: number }>('/api/v1/wallet'),

  team: () => request<TeamMember[]>('/api/v1/team'),

  createTeamMember: (member: { displayName: string; email: string; password: string; roleId: number }) =>
    request<TeamMember>('/api/v1/team', { method: 'POST', body: member }),

  updateTeamMember: (
    id: number,
    patch: { roleId?: number; isActive?: boolean; supervisorId?: number; clearSupervisor?: boolean },
  ) => request<void>(`/api/v1/team/${id}`, { method: 'PATCH', body: patch }),

  /** An empty list means every number — the same convention the API and table use. */
  setTeamChannels: (id: number, channelIds: number[]) =>
    request<void>(`/api/v1/team/${id}/channels`, { method: 'PUT', body: { channelIds } }),

  permissionCatalogue: () => request<PermissionDefinition[]>('/api/v1/permissions'),

  roles: () => request<Role[]>('/api/v1/roles'),

  createRole: (role: { name: string; description?: string; permissions: string[] }) =>
    request<{ id: number }>('/api/v1/roles', { method: 'POST', body: role }),

  updateRole: (id: number, patch: { name?: string; description?: string; permissions?: string[] }) =>
    request<void>(`/api/v1/roles/${id}`, { method: 'PATCH', body: patch }),

  deleteRole: (id: number) => request<void>(`/api/v1/roles/${id}`, { method: 'DELETE' }),

  settings: () => request<TenantSettings>('/api/v1/settings'),

  /** Which plan the team is on and how much of it is in use. */
  myPlan: () => request<MyPlan>('/api/v1/plan'),

  routingFlows: () => request<RoutingFlow[]>('/api/v1/routing/bot-flows'),

  updateSettings: (patch: Partial<TenantSettings>) =>
    request<void>('/api/v1/settings', { method: 'PUT', body: patch }),

  workingHours: () => request<WorkingHour[]>('/api/v1/working-hours'),

  ledger: (beforeId?: number) =>
    request<{ wallet: Wallet; items: LedgerEntry[]; nextCursor: number | null }>(
      `/api/v1/billing/ledger${beforeId ? `?beforeId=${beforeId}` : ''}`),

  statement: (month?: string) =>
    request<Statement>(`/api/v1/billing/statement${month ? `?month=${month}` : ''}`),

  billingProfile: () => request<BillingProfile>('/api/v1/billing/profile'),

  saveBillingProfile: (profile: BillingProfile) =>
    request<void>('/api/v1/billing/profile', { method: 'PUT', body: profile }),

  invoices: () => request<Invoice[]>('/api/v1/billing/invoices'),

  invoice: (id: number) => request<Record<string, unknown>>(`/api/v1/billing/invoices/${id}`),

  /** `amount` is the wallet credit wanted. What is charged is that plus GST. */
  topUp: (amount: number) =>
    request<TopUpOrder>('/api/v1/billing/top-up', { method: 'POST', body: { amount } }),
  /** After Checkout succeeds: verify server-side and credit at once. */
  confirmTopUp: (paymentId: number, result: CheckoutResult) =>
    request<PaymentState>(`/api/v1/billing/top-up/${paymentId}/confirm`, { method: 'POST', body: result }),
  topUpState: (paymentId: number) =>
    request<PaymentState>(`/api/v1/billing/top-up/${paymentId}`),

  reportOverview: (from?: string, to?: string) =>
    request<ReportOverview>(`/api/v1/reports/overview${range(from, to)}`),

  reportAgents: (from?: string, to?: string) =>
    request<AgentReportRow[]>(`/api/v1/reports/agents${range(from, to)}`),

  reportTemplates: (from?: string, to?: string) =>
    request<TemplateReportRow[]>(`/api/v1/reports/templates${range(from, to)}`),

  reportBots: (from?: string, to?: string, name?: string) =>
    request<BotReport>(`/api/v1/reports/bots${range(from, to)}${name ? `${range(from, to) ? '&' : '?'}name=${encodeURIComponent(name)}` : ''}`),

  sla: () => request<SlaStatus>('/api/v1/sla'),

  /** Saves and re-stamps every open conversation; `recomputed` is how many. */
  saveSla: (policy: SlaPolicy) => request<{ recomputed: number }>('/api/v1/sla', { method: 'PUT', body: policy }),

  /** Replaces the whole schedule for one scope. A closed day is a day with no window. */
  setWorkingHours: (channelId: number | null, windows: { dayOfWeek: number; opensAt: string; closesAt: string }[]) =>
    request<void>('/api/v1/working-hours', { method: 'PUT', body: { channelId, windows } }),

  resetTeamPassword: (id: number, password: string) =>
    request<void>(`/api/v1/team/${id}/password`, { method: 'POST', body: { password } }),

  /** Away only. Nobody can declare somebody else Available — see AgentEndpoints. */
  forceAway: (id: number) => request<void>(`/api/v1/team/${id}/away`, { method: 'POST' }),

  /** Hands a mobile-app seat to this person, or takes it back (which signs their phones out). */
  setMobileAccess: (id: number, enabled: boolean) =>
    request<void>(`/api/v1/team/${id}/mobile-access`, { method: 'PUT', body: { enabled } }),

  teamDevices: (id: number) => request<MobileDevice[]>(`/api/v1/team/${id}/devices`),

  /** Cuts one phone off: its sessions end and it cannot sign in again until the owner does so afresh. */
  revokeTeamDevice: (id: number, deviceId: string) =>
    request<void>(`/api/v1/team/${id}/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' }),

  myDevices: () => request<MobileDevice[]>('/api/v1/me/devices'),

  revokeMyDevice: (deviceId: string) =>
    request<void>(`/api/v1/me/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' }),

  agentGroups: () => request<AgentGroup[]>('/api/v1/agent-groups'),

  createAgentGroup: (name: string) =>
    request<{ id: number; name: string }>('/api/v1/agent-groups', { method: 'POST', body: { name } }),

  deleteAgentGroup: (id: number) =>
    request<void>(`/api/v1/agent-groups/${id}`, { method: 'DELETE' }),

  setAgentGroupMembers: (id: number, agentIds: number[]) =>
    request<void>(`/api/v1/agent-groups/${id}/members`, { method: 'PUT', body: { agentIds } }),

  assignmentRules: () => request<AssignmentRule[]>('/api/v1/assignment-rules'),

  createAssignmentRule: (rule: {
    name: string;
    priority?: number;
    matchChannelId?: number | null;
    matchTagId?: number | null;
    matchKeyword?: string | null;
    targetGroupId?: number | null;
  }) => request<{ id: number }>('/api/v1/assignment-rules', { method: 'POST', body: rule }),

  updateAssignmentRule: (id: number, patch: { name?: string; priority?: number; isActive?: boolean }) =>
    request<void>(`/api/v1/assignment-rules/${id}`, { method: 'PATCH', body: patch }),

  deleteAssignmentRule: (id: number) =>
    request<void>(`/api/v1/assignment-rules/${id}`, { method: 'DELETE' }),

  /**
   * Set or replace this agent's reaction. PUT rather than POST because it is
   * idempotent by nature — reacting with the same emoji twice is one reaction,
   * and the server replaces rather than appends.
   */
  setReaction: (conversationId: number, messageId: number, emoji: string) =>
    request<{ messageId: number; emoji: string | null; removed: boolean }>(
      `/api/v1/conversations/${conversationId}/messages/${messageId}/reaction`,
      { method: 'PUT', body: { emoji } },
    ),

  clearReaction: (conversationId: number, messageId: number) =>
    request<{ messageId: number; emoji: string | null; removed: boolean }>(
      `/api/v1/conversations/${conversationId}/messages/${messageId}/reaction`,
      { method: 'DELETE' },
    ),

  /** Reading the thread is reading the mention, so this rides along with opening. */
  markMentionsRead: (conversationId: number) =>
    request<void>(`/api/v1/conversations/${conversationId}/mentions/read`, { method: 'POST' }),

  createTag: (name: string, colour: string) =>
    request<Tag>('/api/v1/tags', { method: 'POST', body: { name, colour } }),

  updateTag: (id: number, patch: { name?: string; colour?: string }) =>
    request<void>(`/api/v1/tags/${id}`, { method: 'PATCH', body: patch }),

  deleteTag: (id: number) => request<void>(`/api/v1/tags/${id}`, { method: 'DELETE' }),

  applyTag: (conversationId: number, tagId: number) =>
    request<void>(`/api/v1/conversations/${conversationId}/tags/${tagId}`, { method: 'POST' }),

  removeTag: (conversationId: number, tagId: number) =>
    request<void>(`/api/v1/conversations/${conversationId}/tags/${tagId}`, { method: 'DELETE' }),
  agents: () => request<Agent[]>('/api/v1/agents'),

  /**
   * The tenant's own numbers. Distinct from adminApi.channels, which reads every
   * tenant's — same word, different scope, and mixing them leaks one client's
   * numbers into another's routing screen.
   */
  channels: () => request<TenantChannel[]>('/api/v1/channels'),
  /** Every number including disconnected ones, with when and why. For the inbox's banner and the Numbers panel. */
  allChannels: () => request<TenantChannel[]>('/api/v1/channels?all=true'),

  /** The team's own name for a number; empty clears it. Needs settings.write. */
  setChannelLabel: (id: number, label: string) =>
    request<{ id: number; label: string | null }>(`/api/v1/channels/${id}/label`, { method: 'PATCH', body: { label } }),

  /** Every non-deleted template, so the picker can explain a blocked one rather
      than silently omitting it. Cached by the caller — this changes rarely. */
  templates: () => request<Template[]>('/api/v1/templates?status='),

  /** One template with its component JSON — what the editor and Duplicate reopen. */
  template: (id: number) => request<Template>(`/api/v1/templates/${id}`),

  createTemplate: (draft: TemplateDraft) =>
    request<{ templateId: number }>('/api/v1/templates', { method: 'POST', body: draft }),

  editTemplate: (id: number, draft: TemplateDraft) =>
    request<{ templateId: number }>(`/api/v1/templates/${id}`, { method: 'POST', body: draft }),

  deleteTemplate: (id: number) => request<void>(`/api/v1/templates/${id}`, { method: 'DELETE' }),

  /** The sample file behind an image, video or document header. Stored only for Meta's review. */
  uploadTemplateSample: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ mediaAssetId: number; fileName: string; mimeType: string }>(
      '/api/v1/templates/sample', { method: 'POST', body: form });
  },

  /** Pulls the catalogue from Meta. Slow enough to warrant its own spinner. */
  syncTemplates: () =>
    request<{ synced: number }>('/api/v1/templates/sync', { method: 'POST' }),

  sendTemplate: (
    conversationId: number,
    templateId: number,
    variables: string[],
    idempotencyKey: string,
    extras: TemplateSendExtras = {},
  ) =>
    request<{ messageId: number; status: string }>('/api/v1/messages/template', {
      method: 'POST',
      // An object, not a string: request() serialises the body itself.
      body: {
        conversationId, templateId, variables, idempotencyKey,
        headerText: extras.headerText ?? null,
        headerMediaAssetId: extras.headerMediaAssetId ?? null,
        buttonParams: extras.buttonParams ?? null,
      },
    }),

  // ------------------------------------------------------------- contacts --

  contacts: (query?: URLSearchParams) =>
    request<{ items: Contact[]; nextAfterId: number | null; nextCursor: string | null }>(
      `/api/v1/contacts${query && [...query].length ? `?${query}` : ''}`,
    ),

  /** How many the same filters select — the count beside the Customers title. */
  contactsCount: (query?: URLSearchParams) =>
    request<{ count: number }>(`/api/v1/contacts/count${query && [...query].length ? `?${query}` : ''}`),

  contactSegments: (id: number) => request<ContactSegment[]>(`/api/v1/contacts/${id}/segments`),

  /** Erases the person: contact, conversations, messages, attachments (F156). Settings permission. */
  eraseContact: (id: number) => request<void>(`/api/v1/contacts/${id}/data`, { method: 'DELETE' }),

  contact: (id: number) => request<Contact>(`/api/v1/contacts/${id}`),

  /**
   * One customer, typed in. The number is normalised the way the import and the
   * webhook normalise it; a 409 carries `body.contactId` of the row that already
   * has that number.
   */
  createContact: (contact: { phone: string; displayName?: string; attributes?: Record<string, string> }) =>
    request<{ id: number; waId: string; displayName: string | null }>('/api/v1/contacts', { method: 'POST', body: contact }),

  updateContact: (id: number, patch: { displayName?: string; attributes?: Record<string, string | null> }) =>
    request<void>(`/api/v1/contacts/${id}`, { method: 'PATCH', body: patch }),

  optOut: (id: number) => request<void>(`/api/v1/contacts/${id}/opt-out`, { method: 'POST' }),

  optIn: (id: number) => request<void>(`/api/v1/contacts/${id}/opt-out`, { method: 'DELETE' }),

  contactFields: () => request<ContactField[]>('/api/v1/contact-fields'),

  /** The key is derived from the label server-side; read it back, never assume it. */
  createContactField: (field: { label: string; fieldType: string; options?: string[]; sortOrder?: number }) =>
    request<{ id: number; fieldKey: string; label: string; fieldType: string }>('/api/v1/contact-fields', {
      method: 'POST',
      body: field,
    }),

  deleteContactField: (id: number) =>
    request<void>(`/api/v1/contact-fields/${id}`, { method: 'DELETE' }),

  // ------------------------------------------------------------- segments --

  segments: () => request<Segment[]>('/api/v1/segments'),

  createSegment: (segment: { name: string; kind: 0 | 1; rules?: SegmentRule[] }) =>
    request<{ id: number; name: string; kind: number }>('/api/v1/segments', { method: 'POST', body: segment }),

  updateSegment: (id: number, patch: { name?: string; rules?: SegmentRule[] }) =>
    request<void>(`/api/v1/segments/${id}`, { method: 'PATCH', body: patch }),

  deleteSegment: (id: number) => request<void>(`/api/v1/segments/${id}`, { method: 'DELETE' }),

  /** Costs a query, so it is asked for one segment at a time rather than in the list. */
  segmentCount: (id: number) => request<{ count: number }>(`/api/v1/segments/${id}/count`),

  /** Who is in a list, a page at a time; rule-based lists included. Paged by (name, id). */
  segmentMembers: (id: number, query: MemberQuery, after: { id: number; name: string | null } | null) => {
    const params = memberParams(query);
    params.set('take', '50');
    if (after) {
      params.set('after', String(after.id));
      if (after.name !== null) params.set('afterName', after.name);
    }
    return request<{ items: SegmentMember[]; nextAfterId: number | null; nextAfterName: string | null }>(
      `/api/v1/segments/${id}/members?${params.toString()}`);
  },

  /** One write over a selection: explicit ids, or everyone the given filter shows. */
  memberActions: (id: number, body: {
    action: MemberAction;
    contactIds?: number[];
    all?: boolean;
    targetSegmentId?: number | null;
    newListName?: string | null;
  } & MemberQuery) =>
    request<{ affected: number; targetSegmentId: number | null }>(
      `/api/v1/segments/${id}/members/actions`, { method: 'POST', body }),

  /** The members a filter shows, as a CSV. */
  exportMembers: (id: number, query: MemberQuery) =>
    requestBlob(`/api/v1/contacts/export?segmentId=${id}&${memberParams(query).toString()}`),

  addSegmentMembers: (id: number, contactIds: number[]) =>
    request<{ added: number }>(`/api/v1/segments/${id}/members`, { method: 'POST', body: { contactIds } }),

  removeSegmentMember: (id: number, contactId: number) =>
    request<void>(`/api/v1/segments/${id}/members/${contactId}`, { method: 'DELETE' }),

  // -------------------------------------------------------------- imports --

  /** The first rows, so the mapping screen can show what is in the file. */
  previewImport: async (file: File): Promise<ImportPreview> => {
    const form = new FormData();
    form.append('file', file);
    return request<ImportPreview>('/api/v1/imports/preview', { method: 'POST', body: form });
  },

  createImport: (job: { fileName: string; content: string; mapping: Record<string, number>; segmentId?: number | null }) =>
    request<{ id: number; status: string }>('/api/v1/imports', { method: 'POST', body: job }),

  imports: () => request<ImportJob[]>('/api/v1/imports'),

  import: (id: number) => request<{ job: ImportJob; problems: ImportProblem[] }>(`/api/v1/imports/${id}`),

  /**
   * The sample to fill in: a workbook with every column the import takes,
   * including this tenant's own custom fields, phone column typed as text.
   */
  importSample: () => requestBlob('/api/v1/imports/sample'),

  /** A file, not JSON — the caller gets a Blob to hand to a download. */
  exportContacts: (segmentId?: number | null) => requestBlob(
    `/api/v1/contacts/export${segmentId ? `?segmentId=${segmentId}` : ''}`,
  ),

  // ------------------------------------------------------------ campaigns --

  campaigns: () => request<Campaign[]>('/api/v1/campaigns'),

  /**
   * Every broadcast ever sent to one list, oldest first — the thread the
   * broadcast screen draws. Its own function rather than a parameter on
   * `campaigns`, because `campaigns` is passed bare as a queryFn and React Query
   * hands a queryFn its context object as the first argument.
   */
  broadcastsTo: (segmentId: number) =>
    request<Campaign[]>(`/api/v1/campaigns?segmentId=${segmentId}&take=200`),

  campaign: (id: number) =>
    request<{ campaign: CampaignDetail; clicks: CampaignClickSummary[] }>(`/api/v1/campaigns/${id}`),

  createCampaign: (campaign: {
    name: string;
    templateId: number;
    channelId: number;
    segmentId?: number | null;
    retargetOfCampaignId?: number | null;
    retargetOutcome?: string | null;
    variableMap?: VariableBinding[];
  }) => request<{ id: number; name: string }>('/api/v1/campaigns', { method: 'POST', body: campaign }),

  updateCampaign: (id: number, patch: {
    name?: string;
    templateId?: number;
    channelId?: number;
    segmentId?: number | null;
    retargetOfCampaignId?: number | null;
    retargetOutcome?: string | null;
    variableMap?: VariableBinding[];
    /** 0 clears the file. */
    headerMediaAssetId?: number | null;
    headerText?: string | null;
  }) => request<void>(`/api/v1/campaigns/${id}`, { method: 'PATCH', body: patch }),

  /**
   * A file for a template header, uploaded on a number rather than in a chat — a
   * campaign has no conversation yet. Goes to Meta first, like every send.
   */
  uploadHeaderMedia: (channelId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ mediaAssetId: number; kind: string; fileName: string }>(
      `/api/v1/media?channelId=${channelId}`, { method: 'POST', body: form },
    );
  },

  deleteCampaign: (id: number) => request<void>(`/api/v1/campaigns/${id}`, { method: 'DELETE' }),

  /** Always an estimate: the audience is resolved again at send time. */
  campaignEstimate: (id: number) => request<CampaignEstimate>(`/api/v1/campaigns/${id}/estimate`),

  /** Rendered against a real contact from the audience, never placeholder text. */
  campaignPreview: (id: number) => request<CampaignPreview>(`/api/v1/campaigns/${id}/preview`),

  launchCampaign: (id: number, scheduledFor?: string | null) =>
    request<{ recipients: number; scheduled: string | null }>(`/api/v1/campaigns/${id}/launch`, {
      method: 'POST',
      body: { scheduledFor: scheduledFor ?? null },
    }),

  pauseCampaign: (id: number) => request<void>(`/api/v1/campaigns/${id}/pause`, { method: 'POST' }),

  resumeCampaign: (id: number) => request<void>(`/api/v1/campaigns/${id}/resume`, { method: 'POST' }),

  cancelCampaign: (id: number) => request<void>(`/api/v1/campaigns/${id}/cancel`, { method: 'POST' }),

  campaignRecipients: (id: number, outcome?: string | null) =>
    request<{ items: CampaignRecipient[]; nextAfterId: number | null }>(
      `/api/v1/campaigns/${id}/recipients${outcome ? `?outcome=${outcome}` : ''}`,
    ),

  /** Act on the people a report line shows: explicit recipient ids, or everyone under the outcome. */
  recipientActions: (id: number, body: {
    action: RecipientAction;
    recipientIds?: number[];
    all?: boolean;
    outcome?: string | null;
    targetSegmentId?: number | null;
    newListName?: string | null;
  }) =>
    request<{ affected: number; targetSegmentId: number | null }>(
      `/api/v1/campaigns/${id}/recipients/actions`, { method: 'POST', body }),

  /** One broadcast's recipients under an outcome, as a CSV. */
  exportRecipients: (id: number, outcome?: string | null) =>
    requestBlob(`/api/v1/contacts/export?campaignId=${id}${outcome && outcome !== 'all' ? `&outcome=${outcome}` : ''}`),
};

function memberParams(query: MemberQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.filter && query.filter !== 'reachable') params.set('filter', query.filter);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.tag) params.set('tag', String(query.tag));
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  return params;
}

/**
 * Forgotten passwords.
 *
 * `forgotPassword` answers 202 whether or not the address exists — deliberately, so
 * the endpoint cannot be used to find out who our clients are. The screen therefore
 * says "if that address is on the system, a link is on its way" and never "no such
 * account", because the API is not telling it which.
 */
export const passwordApi = {
  forgotPassword: (email: string, portal: 'client' | 'platform') =>
    request<void>('/api/v1/auth/forgot-password', { method: 'POST', body: { email, portal }, anonymous: true }),

  resetPassword: (token: string, password: string) =>
    request<void>('/api/v1/auth/reset-password', { method: 'POST', body: { token, password }, anonymous: true }),
};

/**
 * Keys and webhooks — what a tenant's own developer builds against.
 *
 * A created key and a created webhook secret each come back once and are never
 * readable again: one is stored as a hash, the other as ciphertext we sign with.
 */
/** The chat screen's own reads: the timeline, who else is on it, what the bot did. */
export const conversationApi = {
  events: (id: number) => request<ConversationEvent[]>(`/api/v1/conversations/${id}/events`),

  collaborators: (id: number) => request<Collaborator[]>(`/api/v1/conversations/${id}/collaborators`),

  addCollaborator: (id: number, agentId: number) =>
    request<void>(`/api/v1/conversations/${id}/collaborators`, { method: 'POST', body: { agentId } }),

  removeCollaborator: (id: number, agentId: number) =>
    request<void>(`/api/v1/conversations/${id}/collaborators/${agentId}`, { method: 'DELETE' }),

  botRuns: (id: number) => request<BotRun[]>(`/api/v1/conversations/${id}/bot-runs`),


  /** Hides everything up to now from this pane. Deletes nothing — see 0029. */
  clear: (id: number) =>
    request<{ clearedBeforeId: number }>(`/api/v1/conversations/${id}/clear`, { method: 'POST' }),

  /** Our decision to stop, not the customer's. Not opt-out, and never wired to it. */
  /**
   * Blocks here and at WhatsApp. The answer says whether WhatsApp applied it —
   * Meta refuses a block for anyone who has not messaged in the last 24 hours —
   * so the toast can say "blocked at WhatsApp on +91 …" or why it was not.
   */
  block: (contactId: number, reason: string | null) =>
    request<{ whatsapp: string } | void>(`/api/v1/contacts/${contactId}/block`, { method: 'POST', body: { reason } }),

  unblock: (contactId: number) =>
    request<{ whatsapp: string } | void>(`/api/v1/contacts/${contactId}/block`, { method: 'DELETE' }),
};

export const developerApi = {
  apiKeys: () => request<ApiKeySummary[]>('/api/v1/api-keys'),

  createApiKey: (body: { name: string; scopes: string[]; ratePerMinute?: number }) =>
    request<{ id: number; name: string; key: string; keyPrefix: string; scopes: string[] }>(
      '/api/v1/api-keys', { method: 'POST', body }),

  revokeApiKey: (id: number) => request<void>(`/api/v1/api-keys/${id}`, { method: 'DELETE' }),

  webhooks: () => request<WebhookSubscription[]>('/api/v1/webhooks'),

  webhookEvents: () => request<string[]>('/api/v1/webhooks/events'),

  createWebhook: (body: { url: string; events: string[] }) =>
    request<{ id: number; url: string; events: string; secret: string }>(
      '/api/v1/webhooks', { method: 'POST', body }),

  updateWebhook: (id: number, patch: { events?: string[]; isActive?: boolean }) =>
    request<void>(`/api/v1/webhooks/${id}`, { method: 'PATCH', body: patch }),

  deleteWebhook: (id: number) => request<void>(`/api/v1/webhooks/${id}`, { method: 'DELETE' }),

  testWebhook: (id: number) =>
    request<{ deliveryId: number }>(`/api/v1/webhooks/${id}/test`, { method: 'POST' }),

  webhookDeliveries: (id: number) =>
    request<WebhookDelivery[]>(`/api/v1/webhooks/${id}/deliveries`),

  retryDelivery: (id: number) =>
    request<void>(`/api/v1/webhooks/deliveries/${id}/retry`, { method: 'POST' }),
};

export const adminApi = {
  overview: () => request<AdminOverview>('/api/v1/admin/overview'),

  /** Every template on the client's WABA, retired ones included — this is the
      screen where someone asks what happened to the one they submitted. */
  templates: (tenantId: number) =>
    request<AdminTemplate[]>(`/api/v1/admin/tenants/${tenantId}/templates`),

  createTemplate: (tenantId: number, draft: TemplateDraft) =>
    request<{ templateId: number }>(`/api/v1/admin/tenants/${tenantId}/templates`, {
      method: 'POST',
      body: draft,
    }),

  editTemplate: (templateId: number, draft: TemplateDraft) =>
    request<{ templateId: number }>(`/api/v1/admin/templates/${templateId}`, {
      method: 'POST',
      body: draft,
    }),

  deleteTemplate: (templateId: number) =>
    request<void>(`/api/v1/admin/templates/${templateId}`, { method: 'DELETE' }),

  syncTemplates: (tenantId: number) =>
    request<{ synced: number }>(`/api/v1/admin/tenants/${tenantId}/templates/sync`, { method: 'POST' }),

  tenants: () => request<AdminTenant[]>('/api/v1/admin/tenants'),
  channels: () => request<AdminChannel[]>('/api/v1/admin/channels'),

  defaultPricing: () => request<CategoryPrice[]>('/api/v1/admin/pricing'),

  setDefaultPrice: (category: string, unitPrice: number) =>
    request<void>('/api/v1/admin/pricing', { method: 'PUT', body: { category, unitPrice } }),

  tenantPricing: (tenantId: number) =>
    request<CategoryPrice[]>(`/api/v1/admin/tenants/${tenantId}/pricing`),

  plans: () => request<Plan[]>('/api/v1/admin/plans'),

  createPlan: (body: PlanBody) =>
    request<{ id: number }>('/api/v1/admin/plans', { method: 'POST', body }),

  updatePlan: (id: number, body: PlanBody) =>
    request<void>(`/api/v1/admin/plans/${id}`, { method: 'PATCH', body }),

  tenantSubscription: (tenantId: number) =>
    request<TenantSubscriptionView>(`/api/v1/admin/tenants/${tenantId}/subscription`),

  assignSubscription: (tenantId: number, body: {
    planId: number; startsOn?: string; months?: number; extraAgents?: number; extraNumbers?: number;
    annualPrice?: number; notes?: string; mobileSeats?: number;
  }) => request<{ id: number; startsOn: string; endsOn: string; reactivated: boolean }>(
    `/api/v1/admin/tenants/${tenantId}/subscription`, { method: 'POST', body }),

  adjustSubscription: (tenantId: number, body: { extraAgents?: number; extraNumbers?: number; endsOn?: string; notes?: string; mobileSeats?: number }) =>
    request<void>(`/api/v1/admin/tenants/${tenantId}/subscription`, { method: 'PATCH', body }),

  setTenantPrice: (tenantId: number, category: string, unitPrice: number) =>
    request<void>(`/api/v1/admin/tenants/${tenantId}/pricing`, { method: 'PUT', body: { category, unitPrice } }),

  adjustWallet: (tenantId: number, amount: number, reason: string) =>
    request<{ balance: number }>(`/api/v1/admin/tenants/${tenantId}/wallet/adjust`,
      { method: 'POST', body: { amount, reason } }),

  suspendWallet: (tenantId: number, active: boolean, reason?: string) =>
    request<void>(`/api/v1/admin/tenants/${tenantId}/wallet/suspend`,
      { method: 'POST', body: { active, reason } }),

  createTenant: (body: {
    companyName: string; slug?: string; contactEmail: string; contactPhone?: string;
    adminDisplayName: string; adminEmail: string; adminPassword: string;
  }) => request<{
    id: number; slug: string; companyName: string; contactEmail: string;
    administrator: { id: number; email: string; displayName: string };
  }>('/api/v1/admin/tenants', { method: 'POST', body }),

  updateTenant: (id: number, patch: { companyName?: string; contactEmail?: string; status?: number }) =>
    request<void>(`/api/v1/admin/tenants/${id}`, { method: 'PATCH', body: patch }),

  metaApps: () => request<MetaApp[]>('/api/v1/admin/meta-apps'),

  createMetaApp: (body: {
    name: string; metaAppId: string; tenantId?: number | null;
    appSecret?: string; webhookSlug?: string; graphVersion?: string;
  }) => request<{
    id: number; name: string; metaAppId: string; webhookSlug: string;
    tenantId: number | null; verifyToken: string; webhookPath: string;
  }>('/api/v1/admin/meta-apps', { method: 'POST', body }),

  checkChannel: (body: { metaAppId: number; phoneNumberId: string; accessToken: string }) =>
    request<ChannelCheck>('/api/v1/admin/channels/check', { method: 'POST', body }),

  createChannel: (body: {
    tenantId: number; metaAppId: number; wabaId: string; phoneNumberId: string;
    displayPhoneNumber?: string; accessToken: string; force?: boolean;
    /** Six digits. Registers the number with the Cloud API in the same step; without it the number cannot send. */
    pin?: string;
  }) => request<ChannelCreated>('/api/v1/admin/channels', { method: 'POST', body }),

  /**
   * Registers a number that exists here but answers 133010 at Meta, and
   * re-subscribes its WABA to the app's webhooks. The PIN becomes the number's
   * two-step verification PIN; a number that already has one needs that one.
   */
  registerChannel: (id: number, pin: string) =>
    request<{ registered: boolean; registerProblem: string | null; subscribed: boolean; subscribeProblem: string | null }>(
      `/api/v1/admin/channels/${id}/register`, { method: 'POST', body: { pin } }),

  /**
   * The two values a Meta app only acquires later: the Embedded Signup
   * configuration id, which cannot exist until the app is an approved Tech
   * Provider, and a rotated app secret. Pass `esConfigId: null` to clear it;
   * omitting the field leaves it alone.
   */
  updateMetaApp: (id: number, patch: { appSecret?: string; esConfigId?: string | null; isActive?: boolean }) =>
    request<void>(`/api/v1/admin/meta-apps/${id}`, { method: 'PATCH', body: patch }),

  /**
   * What the browser needs to build Meta's dialog: the app id to initialise the SDK
   * with and the Login-for-Business configuration id that puts the coexistence
   * feature in it. Both are public — they appear in the page source of every business
   * running Embedded Signup — which is why this is a GET and not part of the connect
   * call.
   */
  signupConfig: (metaAppRowId: number) => request<{
    metaAppId: string; configId: string | null; graphVersion: string;
    name: string; hasAppSecret: number;
  }>(`/api/v1/admin/meta-apps/${metaAppRowId}/signup-config`),

  /**
   * Hands Meta's code straight back to the server. Nothing may sit between the popup
   * closing and this call — the code lives about thirty seconds.
   */
  connectCoexistence: (body: {
    tenantId: number; metaAppId: number; code: string;
    wabaId: string; phoneNumberId: string; historyMonths?: number;
  }) => request<{
    id: number; verified: boolean; problem: string | null; subscribeProblem: string | null;
    isActive: boolean; verifiedName: string | null; displayPhoneNumber: string | null;
    sendRatePerSecond: number; isCoexistence: boolean; historyMonths: number;
    /** The number already had a channel here that Meta had offboarded; it was brought back, not added. */
    reconnected: boolean;
    /** The number was already live here; only its token and identity were refreshed. */
    refreshed: boolean;
  }>('/api/v1/admin/channels/coexistence', { method: 'POST', body }),

  updateChannel: (id: number, patch: { accessToken?: string; isActive?: boolean; sendRatePerSecond?: number; force?: boolean }) =>
    request<{ id: number; verified: boolean | null; problem: string | null }>(
      `/api/v1/admin/channels/${id}`, { method: 'PATCH', body: patch }),
  // ------------------------------------------------------ platform admin --

  audit: (params: {
    tenantId?: number | null; action?: string | null; actorId?: number | null;
    actorType?: number | null; from?: string | null; to?: string | null;
    beforeId?: number | null; take?: number;
  } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined && value !== '') query.set(key, String(value));
    }
    const suffix = query.toString();
    return request<AuditPage>(`/api/v1/admin/audit${suffix ? `?${suffix}` : ''}`);
  },

  auditActions: () => request<string[]>('/api/v1/admin/audit/actions'),

  platformOverview: (from?: string, to?: string) =>
    request<PlatformOverview>(`/api/v1/admin/reports/overview${range(from, to)}`),

  platformTenants: (from?: string, to?: string) =>
    request<TenantMoneyRow[]>(`/api/v1/admin/reports/tenants${range(from, to)}`),

  suspendTenant: (id: number, reason: string) =>
    request<void>(`/api/v1/admin/tenants/${id}/suspend`, { method: 'POST', body: { reason } }),

  reactivateTenant: (id: number) =>
    request<void>(`/api/v1/admin/tenants/${id}/reactivate`, { method: 'POST' }),

  createTenantAgent: (tenantId: number, body: { email: string; displayName: string; password: string }) =>
    request<{ id: number; email: string; displayName: string; role: string }>(
      `/api/v1/admin/tenants/${tenantId}/agents`, { method: 'POST', body }),

  disableChannel: (id: number) =>
    request<void>(`/api/v1/admin/channels/${id}/disable`, { method: 'POST' }),

  /** The catalogue switches and the India compliance record, read from Meta each time. */
  channelCommerce: (id: number) => request<ChannelCommerce>(`/api/v1/admin/channels/${id}/commerce`),

  setChannelCommerce: (id: number, body: { isCatalogVisible?: boolean; isCartEnabled?: boolean }) =>
    request<void>(`/api/v1/admin/channels/${id}/commerce`, { method: 'PUT', body }),

  /** Files the Consumer Protection (E-Commerce) Rules record with Meta for a number in India. */
  setChannelCompliance: (id: number, body: ComplianceInfo) =>
    request<void>(`/api/v1/admin/channels/${id}/compliance`, { method: 'PUT', body }),

  recheckChannel: (id: number) =>
    request<{
      ok: boolean; problem?: string | null; verifiedName?: string | null;
      displayPhoneNumber?: string | null; qualityRating?: string | null;
      messagingLimitTier?: string | null; suggestedRatePerSecond?: number;
    }>(`/api/v1/admin/channels/${id}/recheck`, { method: 'POST' }),

  platformUsers: () => request<PlatformUser[]>('/api/v1/admin/platform-users'),

  createPlatformUser: (body: {
    email: string; displayName: string; password: string; isSuperuser: boolean;
  }) => request<{ id: number }>('/api/v1/admin/platform-users', { method: 'POST', body }),

  updatePlatformUser: (id: number, patch: { displayName?: string; isActive?: boolean; isSuperuser?: boolean }) =>
    request<void>(`/api/v1/admin/platform-users/${id}`, { method: 'PATCH', body: patch }),

  errors: (take = 100) => request<AdminErrorEvent[]>(`/api/v1/admin/errors?take=${take}`),
  jobs: (take = 50) => request<AdminJobRun[]>(`/api/v1/admin/jobs?take=${take}`),

  /** Puts exhausted webhook events back on the queue. The payloads are kept, so
      anything that failed for a reason since fixed is fully recoverable. */
  retryFailedWebhooks: () =>
    request<{ requeued: number }>('/api/v1/admin/webhooks/retry-failed', { method: 'POST' }),

  // ----------------------------------------------------------------- bots --

  flows: (tenantId: number) => request<BotFlowSummary[]>(`/api/v1/admin/tenants/${tenantId}/flows`),

  flow: (id: number) => request<{ flow: BotFlow; report: FlowReport }>(`/api/v1/admin/flows/${id}`),

  createFlow: (tenantId: number, flow: { name: string; channelId?: number | null; definition?: string }) =>
    request<{ id: number; name: string; version: number }>(`/api/v1/admin/tenants/${tenantId}/flows`, {
      method: 'POST', body: flow,
    }),

  saveFlow: (id: number, patch: { name?: string; definition?: string; channelId?: number | null; channelSpecified?: boolean }) =>
    request<void>(`/api/v1/admin/flows/${id}`, { method: 'PUT', body: patch }),

  publishFlow: (id: number) =>
    request<{ published: number; draft: number; report: FlowReport }>(`/api/v1/admin/flows/${id}/publish`, { method: 'POST' }),

  setFlowEntry: (id: number, body: BotEntry) =>
    request<void>(`/api/v1/admin/flows/${id}/entry`, { method: 'PUT', body }),
  enableFlow: (id: number) => request<void>(`/api/v1/admin/flows/${id}/enable`, { method: 'POST' }),

  disableFlow: (id: number) => request<void>(`/api/v1/admin/flows/${id}/disable`, { method: 'POST' }),

  deleteFlow: (id: number) => request<void>(`/api/v1/admin/flows/${id}`, { method: 'DELETE' }),

  /** Validates without saving — what the builder calls as the definition is edited. */
  validateFlow: (tenantId: number, definition: string) =>
    request<FlowReport>(`/api/v1/admin/tenants/${tenantId}/flows/validate`, {
      method: 'POST', body: { definition },
    }),

  rehearseFlow: (tenantId: number, body: RehearseRequest) =>
    request<RehearsalTurn>(`/api/v1/admin/tenants/${tenantId}/flows/rehearse`, { method: 'POST', body }),

  testBotAction: (tenantId: number, id: number, body: { variables?: Record<string, unknown>; body?: unknown; live?: boolean }) =>
    request<ActionTestResult>(`/api/v1/admin/tenants/${tenantId}/bot-actions/${id}/test`, { method: 'POST', body }),

  botActions: (tenantId: number) => request<BotAction[]>(`/api/v1/admin/tenants/${tenantId}/bot-actions`),

  saveBotAction: (tenantId: number, action: {
    actionKey: string; name?: string; description?: string; method?: string; url: string;
    headers?: Record<string, string>; authType?: string | null; authHeader?: string | null;
    /** Omit to leave the stored credential alone. */
    secret?: string;
  }) => request<{ id: number; actionKey: string }>(`/api/v1/admin/tenants/${tenantId}/bot-actions`, {
    method: 'POST', body: action,
  }),

  deleteBotAction: (id: number) => request<void>(`/api/v1/admin/bot-actions/${id}`, { method: 'DELETE' }),

  botSessions: (tenantId: number) => request<BotSessionRow[]>(`/api/v1/admin/tenants/${tenantId}/bot-sessions`),
};

/**
 * The same bot surface, from a client's own portal.
 *
 * Every route here is scoped to the caller's tenant by the server, so nothing takes a
 * tenant id and nothing can be pointed at another tenant's flow by changing a number
 * in a URL. That is why this is a separate object rather than adminApi with a
 * different base: the shapes are the same, the addresses are not, and one object
 * carrying both would put "which tenant?" back into the caller's hands.
 */

/** One thing the bot said, or one call it made, during a rehearsal. */
export interface RehearsalOutput {
  kind: 'text' | 'list' | 'buttons' | 'products' | 'call';
  text?: string | null;
  body?: string | null;
  header?: string | null;
  footer?: string | null;
  button?: string | null;
  sectionTitle?: string | null;
  rows?: { id: string; title: string; description?: string | null }[] | null;
  action?: string | null;
  method?: string | null;
  url?: string | null;
  status?: number | null;
  simulated?: boolean;
  note?: string | null;
  /** The parsed response body, when the call really ran. */
  data?: unknown;
}

export interface RehearsalTurn {
  outputs: RehearsalOutput[];
  state: string | null;
  variables: Record<string, unknown>;
  ended: boolean;
  handedOff: boolean;
}

/** What one turn of the emulator sends. */
export interface RehearseRequest {
  definition: string;
  state?: string | null;
  variables?: Record<string, unknown>;
  kind?: 'text' | 'selection' | 'other';
  value?: string | null;
  reset?: boolean;
  /** 'none' (default), 'reads' — GET and HEAD for real — or 'all'. */
  calls?: 'none' | 'reads' | 'all';
}

export interface ActionTestResult {
  actionKey: string;
  call: RehearsalOutput | null;
  variables: Record<string, unknown>;
}

export const botApi = {
  flows: () => request<BotFlowSummary[]>('/api/v1/bots/flows'),

  flow: (id: number) => request<{ flow: BotFlow; report: FlowReport }>(`/api/v1/bots/flows/${id}`),

  createFlow: (flow: { name: string; channelId?: number | null; definition?: string }) =>
    request<{ id: number; name: string; version: number }>('/api/v1/bots/flows', { method: 'POST', body: flow }),

  saveFlow: (id: number, patch: { name?: string; definition?: string; channelId?: number | null; channelSpecified?: boolean }) =>
    request<void>(`/api/v1/bots/flows/${id}`, { method: 'PUT', body: patch }),

  publishFlow: (id: number) =>
    request<{ published: number; draft: number; report: FlowReport }>(`/api/v1/bots/flows/${id}/publish`, { method: 'POST' }),

  setFlowEntry: (id: number, body: BotEntry) =>
    request<void>(`/api/v1/bots/flows/${id}/entry`, { method: 'PUT', body }),
  enableFlow: (id: number) => request<void>(`/api/v1/bots/flows/${id}/enable`, { method: 'POST' }),

  disableFlow: (id: number) => request<void>(`/api/v1/bots/flows/${id}/disable`, { method: 'POST' }),

  /** Only a draft: a published version stays so the conversations that ran on it can be read. */
  deleteFlow: (id: number) => request<void>(`/api/v1/bots/flows/${id}`, { method: 'DELETE' }),

  validateFlow: (definition: string) =>
    request<FlowReport>('/api/v1/bots/flows/validate', { method: 'POST', body: { definition } }),

  /** Drives the draft in the editor through one turn, without WhatsApp. */
  rehearseFlow: (body: RehearseRequest) =>
    request<RehearsalTurn>('/api/v1/bots/flows/rehearse', { method: 'POST', body }),

  testAction: (id: number, body: { variables?: Record<string, unknown>; body?: unknown; live?: boolean }) =>
    request<ActionTestResult>(`/api/v1/bots/actions/${id}/test`, { method: 'POST', body }),

  actions: () => request<BotAction[]>('/api/v1/bots/actions'),

  saveAction: (action: {
    actionKey: string; name?: string; description?: string; method?: string; url: string;
    headers?: Record<string, string>; authType?: string | null; authHeader?: string | null;
    /** Omit to leave the stored credential alone. It is never sent back out. */
    secret?: string;
  }) => request<{ id: number; actionKey: string }>('/api/v1/bots/actions', { method: 'POST', body: action }),

  deleteAction: (id: number) => request<void>(`/api/v1/bots/actions/${id}`, { method: 'DELETE' }),

  sessions: () => request<BotSessionRow[]>('/api/v1/bots/sessions'),

  /** How a bot is doing over the last N days. Keyed on its name, across versions. */
  funnel: (name: string, days = 30) =>
    request<BotFunnel>(`/api/v1/bots/${encodeURIComponent(name)}/funnel?days=${days}`),
};

/**
 * The WhatsApp Shop, merchant side (docs/shop/PSM-WhatsApp-Shop-Implementation-Flows.pdf §11).
 * Public /s/{slug} routes are not here: the shop page is its own app.
 */
export const shopApi = {
  settings: () => request<{ settings: ShopSettings | null; shopUrl: string; themePresets: string[]; gateways: string[] }>('/api/v1/shop/settings'),
  saveSettings: (input: ShopSettingsInput) => request<ShopSettings>('/api/v1/shop/settings', { method: 'PUT', body: input }),
  slugCheck: (slug: string | null, name: string | null) =>
    request<{ slug: string; valid: boolean; available: boolean; suggestion: string | null }>(
      `/api/v1/shop/slug-check?${slug ? `slug=${encodeURIComponent(slug)}` : ''}${name ? `&name=${encodeURIComponent(name)}` : ''}`),

  groups: () => request<CustomerGroup[]>('/api/v1/shop/groups'),
  createGroup: (input: CustomerGroupInput) => request<CustomerGroup>('/api/v1/shop/groups', { method: 'POST', body: input }),
  updateGroup: (id: number, input: CustomerGroupInput) => request<CustomerGroup>(`/api/v1/shop/groups/${id}`, { method: 'PUT', body: input }),
  setDefaultGroup: (id: number) => request<CustomerGroup>(`/api/v1/shop/groups/${id}/default`, { method: 'POST' }),
  deleteGroup: (id: number, moveTo?: number | null) =>
    request<void>(`/api/v1/shop/groups/${id}${moveTo ? `?moveTo=${moveTo}` : ''}`, { method: 'DELETE' }),

  categories: () => request<ProductCategory[]>('/api/v1/shop/categories'),
  createCategory: (name: string) => request<ProductCategory>('/api/v1/shop/categories', { method: 'POST', body: { name } }),
  updateCategory: (id: number, name: string, sortOrder?: number) =>
    request<ProductCategory>(`/api/v1/shop/categories/${id}`, { method: 'PUT', body: { name, sortOrder } }),
  deleteCategory: (id: number) => request<void>(`/api/v1/shop/categories/${id}`, { method: 'DELETE' }),

  products: (params: { q?: string; categoryId?: number | null; active?: boolean | null; skip?: number; take?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.categoryId) qs.set('categoryId', String(params.categoryId));
    if (params.active !== undefined && params.active !== null) qs.set('active', String(params.active));
    if (params.skip) qs.set('skip', String(params.skip));
    if (params.take) qs.set('take', String(params.take));
    const query = qs.toString();
    return request<{ items: ProductListItem[]; total: number }>(`/api/v1/shop/products${query ? `?${query}` : ''}`);
  },
  product: (id: number) => request<ProductDetail>(`/api/v1/shop/products/${id}`),
  createProduct: (input: ShopProductInput) => request<ProductDetail>('/api/v1/shop/products', { method: 'POST', body: input }),
  updateProduct: (id: number, input: ShopProductInput) => request<ProductDetail>(`/api/v1/shop/products/${id}`, { method: 'PUT', body: input }),
  deleteProduct: (id: number) => request<void>(`/api/v1/shop/products/${id}`, { method: 'DELETE' }),
  uploadImage: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ url: string }>('/api/v1/shop/images', { method: 'POST', body: form });
  },

  prices: (productId: number) => request<PriceRow[]>(`/api/v1/shop/products/${productId}/prices`),
  savePrices: (productId: number, rows: PriceRowInput[]) =>
    request<PriceRow[]>(`/api/v1/shop/products/${productId}/prices`, { method: 'PUT', body: { rows } }),
  contactPrices: (contactId: number) => request<PriceRow[]>(`/api/v1/shop/contacts/${contactId}/prices`),
  saveContactPrices: (contactId: number, rows: PriceRowInput[]) =>
    request<PriceRow[]>(`/api/v1/shop/contacts/${contactId}/prices`, { method: 'PUT', body: { rows } }),

  importSample: () => requestBlob('/api/v1/shop/import/sample'),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<ShopImportPreview>('/api/v1/shop/import/preview', { method: 'POST', body: form });
  },
  importApply: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<ShopImportPreview>('/api/v1/shop/import/apply', { method: 'POST', body: form });
  },

  /** The shop link, with a contact token when a contact is named. */
  link: (contactId?: number | null) =>
    request<{ url: string; expiresAt: string | null }>(`/api/v1/shop/link${contactId ? `?contactId=${contactId}` : ''}`),
};

export const systemApi = {
  readiness: () => request<{ status: string; migrationsApplied: number }>('/health/ready'),
  status: () => request<{ service: string; environment: string; version: string }>('/api/v1/status'),
};

/**
 * Turns an API-relative path into one the browser can actually fetch.
 *
 * The API returns media links as paths, because it sits behind a reverse proxy and
 * cannot reliably know its own public origin. The portals are served from a
 * different origin, so a raw path resolves against the *frontend* — where it hits
 * the SPA fallback and comes back as index.html rather than an image. Anything
 * used as a src or href goes through here.
 */
export function apiUrl(path: string): string {
  if (!path) return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${BASE_URL}${path}`;
}

/**
 * The same signed media link as a file save rather than an inline render. The
 * API is on another origin, so `<a download>` is ignored by every browser; the
 * server has to send Content-Disposition: attachment, and `download=1` asks it to.
 */
export function mediaDownloadUrl(mediaUrl: string): string {
  const absolute = apiUrl(mediaUrl);
  return `${absolute}${absolute.includes('?') ? '&' : '?'}download=1`;
}

export { BASE_URL };
